import { PURCHASER_API_BASE_URL, getHeaders } from "../Constants";
import type { User } from "./Auth";
import type { FileUpload } from "./FileUpload";

export interface FileComparison {
    id: number;
    publicId: number;
    teamId: number;
    userId: number;
    createdBy: User;
    createdAt: string;
    updatedAt: string;
    title: string;
    description: string;
    commonFieldsDiscoveredAt: string;
    commonFieldsErroredAt: string;
    fileComparisonQuote: FileComparisonQuote[];
    fileComparisonCommonFields: FileComparisonCommonField[];
    genericComparisons: GenericComparison[];
}

export interface GenericComparison {
    id: number;
    createdAt: string;
    rawOutput: string;
    tableContent: string;
}

interface FileComparisonCommonField {
    id: number;
    name: string;
    fileComparisonId: number;
    quotes: CommonFieldAssociation[];
}

interface CommonFieldAssociation {
    quoteId: number;
    lineItemId: number;
}

export interface FileComparisonQuote {
    id: number;
    fileComparisonId: number;
    vendorQuoteRef: string;
    supplierName: string;
    supplierPhone: string;
    supplierAddress: string;
    salesRepName: string;
    quotedAt: string;
    shippingTerms: string;
    normalizedShippingTerms: string[];
    paymentTerms: string;
    normalizedPaymentTerms: string[];
    validUntil: null;
    normalizedValidUntil: null;
    subtotal: number;
    total: number;
    currencyCode: string;
    leadTime: null;
    leadTimeMinDays: null;
    leadTimeMaxDays: null;
    processedAt: string;
    erroredAt: string;
    fileId: number;
    file: FileUpload;
    intendedFileName: string;
    uploadErroredAt: string;
}

export default {
    getQuoteComparisons: (): Promise<FileComparison[]> =>
        fetch(`${PURCHASER_API_BASE_URL}/compare`, {
            headers: getHeaders(),
        }).then(res => res.json()),
    getQuoteComparison: (publicComparisonId: number): Promise<FileComparison> =>
        fetch(`${PURCHASER_API_BASE_URL}/compare/${publicComparisonId}`, {
            headers: getHeaders(),
        }).then(res => res.json()),
    uploadQuoteCompareFiles: (comparisonId: number | string, files: FormData): Promise<Response> =>
        fetch(`${PURCHASER_API_BASE_URL}/compare/${comparisonId}`, {
            method: "POST",
            headers: {
                Authorization: getHeaders().Authorization,
            },
            body: files,
        }),
    deleteQuoteComparison: (comparisonId: number): Promise<void> =>
        fetch(`${PURCHASER_API_BASE_URL}/compare/${comparisonId}`, {
            method: "DELETE",
            headers: getHeaders(),
            body: JSON.stringify({}),
        }).then(res => res.json()),
    getExcelFile: (comparisonId: number) =>
        fetch(`${PURCHASER_API_BASE_URL}/compare/${comparisonId}/xlsx/generic`, {
            headers: getHeaders(),
        }).then(res => res.blob()),
    getComparisonPdf: (comparisonId: number) =>
        fetch(`${PURCHASER_API_BASE_URL}/compare/${comparisonId}/pdf/generic`, {
            headers: getHeaders(),
        }).then(res => res.blob()),
    getQuoteCompareDocuments: (
        comparisonId: number,
    ): Promise<
        {
            url: {
                url: string;
                expiresOn: string;
            };
            file: File;
        }[]
    > =>
        fetch(`${PURCHASER_API_BASE_URL}/compare/${comparisonId}/documents`, {
            headers: getHeaders(),
        }).then(res => res.json()),

    // V3 routes
    getComparison: (publicId: string, language?: string): Promise<ComparisonView> =>
        fetch(`${PURCHASER_API_BASE_URL}/comparisons/${publicId}${language ? `?lang=${language}` : ""}`, { headers: getHeaders() }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to fetch comparison");
            }
            return res.json();
        }),
    createComparison: (data: FormData) =>
        fetch(`${PURCHASER_API_BASE_URL}/comparisons`, {
            method: "POST",
            headers: {
                Authorization: getHeaders().Authorization,
            },
            body: data,
        }).then(res => {
            if (!res.ok) {
                throw new Error(`Failed to create comparison: ${res.status}`);
            }
            return res.json();
        }),
    addComparisonFiles: (publicId: string, data: FormData) =>
        fetch(`${PURCHASER_API_BASE_URL}/comparisons/${publicId}/files`, {
            method: "POST",
            headers: {
                Authorization: getHeaders().Authorization,
            },
            body: data,
        }).then(res => res.json()),
    deleteComparison: (publicId: string) =>
        fetch(`${PURCHASER_API_BASE_URL}/comparisons/${publicId}`, {
            method: "DELETE",
            headers: getHeaders(),
            body: JSON.stringify({}),
        }),
    getComparisons: (): Promise<{ comparisons: ComparisonListView[] }> =>
        fetch(`${PURCHASER_API_BASE_URL}/comparisons`, {
            headers: getHeaders(),
        }).then(res => res.json()),
    setComparisonShared: (publicId: string, share: boolean) =>
        fetch(`${PURCHASER_API_BASE_URL}/comparisons/${publicId}/share`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({ share }),
        }).then(res => res.json()),
    exportComparison: (publicId: string, format: string, language: string, options?: ExportComparisonOptions) => {
        const params = new URLSearchParams({
            format,
            lang: language,
            orientation: options?.orientation || "portrait",
            singlePage: String(options?.singlePage ?? false),
            includeLabels: String(options?.includeLabels ?? false),
            includeStats: String(options?.includeStats ?? false),
            includeAttributes: String(options?.includeAttributes ?? false),
            includeBranding: String(options?.includeBranding ?? false),
        });
        if (options?.blockTypes) {
            params.set("blockTypes", options.blockTypes.join(","));
        }
        return fetch(`${PURCHASER_API_BASE_URL}/comparisons/${publicId}/export?${params}`, {
            headers: getHeaders(),
        }).then(res => res.blob());
    },
    exportComparisonBlock: (publicId: string, blockId: number) =>
        fetch(`${PURCHASER_API_BASE_URL}/comparisons/${publicId}/blocks/${blockId}/export?format=pdf`, {
            headers: getHeaders(),
        }).then(res => {
            if (!res.ok) {
                throw new Error(`Failed to export block: ${res.status}`);
            }
            return res.blob();
        }),
    renameComparison: (publicId: string, title: string) =>
        fetch(`${PURCHASER_API_BASE_URL}/comparisons/${publicId}/rename`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({ title }),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to rename comparison");
            }
            return res.json();
        }),
    reprocessComparison: (publicId: string) =>
        fetch(`${PURCHASER_API_BASE_URL}/comparisons/${publicId}/reprocess`, {
            method: "PUT",
            headers: getHeaders(),
            body: JSON.stringify({}),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to reprocess comparison");
            }
            return res.json();
        }),
};

export type ComparisonListView = {
    publicId: number;
    title: string;
    summary: string;
    shareKey: string;
    createdBy: User;
    createdAt: string;
    updatedAt: string;
    files: ComparisonFile[];
    processedBlocks: number;
    totalBlocks: number;
};

export type ComparisonView = {
    publicId: number;
    blocks: ComparisonBlockView[];
    files: ComparisonFile[];
    summary: string;
    title: string;
    shareKey: string;
    secondsRemaining: number | null;
};

export type ComparisonBlockView = {
    id: number;
    type: string;
    content: string | null;
    metadata: any[];
    language: string;
    fileBlocks: ComparisonFileBlockView[];
};

type ComparisonFileBlockView = {
    id: number;
    originalFilename: string;
    fileId: number;
    content: string | null;
    metadata: { currency: string; vendor_name: string; line_items: any[] };
    blockType: string;
};

export type ComparisonFile = {
    id: number;
    fileUploadId: number;
    createdAt: string;
    mimeType: string;
    fileSize: string | null;
    originalFilename: string;
    internalFilename: string;
    isRead: boolean;
    processedBlockTypes: string[];
    pendingBlockTypes: string[];
};

export interface ExportComparisonOptions {
    orientation?: "portrait" | "landscape";
    singlePage?: boolean;
    includeLabels?: boolean;
    includeStats?: boolean;
    includeAttributes?: boolean;
    includeBranding?: boolean;
    blockTypes?: string[];
}
